import React, { FC, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { ReferentialsSection, StandardParametersForm } from "./components";
import type {
  AnalysisParameters,
  EducationTrainingItem,
  ProfessionalPositionItem,
  GradingItem,
} from "../../hooks";

import { useGetCurrencies } from "@api";
import { useUserStore } from "@store/user";

export const MAX_EDUCATION_ROWS = 8;
export const MAX_PROFESSIONAL_ROWS = 5;

type PageType = "standard" | "referentials";

interface PaygapAnalysisParametersProps {
  parameters: AnalysisParameters;
  onParameterChange: (
    field: keyof AnalysisParameters,
    value: string | number,
  ) => void;
  onEducationAndTrainingChange: (
    index: number,
    field: keyof EducationTrainingItem,
    value: string | number,
  ) => void;
  onProfessionalPositionChange: (
    index: number,
    field: keyof ProfessionalPositionItem,
    value: string | number,
  ) => void;
  onAddEducationAndTraining: () => void;
  onRemoveEducationAndTraining: (index: number) => void;
  onAddProfessionalPosition: () => void;
  onRemoveProfessionalPosition: (index: number) => void;
  onGrading1Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading2Change: (index: number, field: keyof GradingItem, value: string) => void;
  onGrading3Change: (index: number, field: keyof GradingItem, value: string) => void;
  onAddGrading1: () => void;
  onAddGrading2: () => void;
  onAddGrading3: () => void;
  onRemoveGrading1: (index: number) => void;
  onRemoveGrading2: (index: number) => void;
  onRemoveGrading3: (index: number) => void;
  onSaveReferentials: () => void;
  hasIdConflicts: boolean;
  showSaveSuccess: boolean;
}

const PaygapAnalysisParameters: FC<PaygapAnalysisParametersProps> = ({
  parameters,
  onParameterChange,
  onEducationAndTrainingChange,
  onProfessionalPositionChange,
  onAddEducationAndTraining,
  onRemoveEducationAndTraining,
  onAddProfessionalPosition,
  onRemoveProfessionalPosition,
  onGrading1Change,
  onGrading2Change,
  onGrading3Change,
  onAddGrading1,
  onAddGrading2,
  onAddGrading3,
  onRemoveGrading1,
  onRemoveGrading2,
  onRemoveGrading3,
  onSaveReferentials,
  hasIdConflicts,
  showSaveSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { currencies, currenciesEnabled } = useGetCurrencies(user);
  const [activePage, setActivePage] = useState<PageType>("standard");

  const pageTitle =
    activePage === "standard"
      ? t("paygap.parameters.page.standard")
      : t("paygap.parameters.page.referentials");

  return (
    <Box mb={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={1}
      >
        <Typography variant="subtitle1" gutterBottom>
          {t("paygap.parameters.title")}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            aria-label="Previous"
            onClick={() => setActivePage("standard")}
            disabled={activePage === "standard"}
            size="small"
          >
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
          <Typography variant="body2">{pageTitle}</Typography>
          <IconButton
            aria-label="Next"
            onClick={() => setActivePage("referentials")}
            disabled={activePage === "referentials"}
            size="small"
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {activePage === "standard" ? (
        <StandardParametersForm
          parameters={parameters}
          onParameterChange={onParameterChange}
          currencies={currencies}
          currenciesEnabled={currenciesEnabled}
          onSave={onSaveReferentials}
          showSaveSuccess={showSaveSuccess}
        />
      ) : (
        <ReferentialsSection
          parameters={parameters}
          onEducationAndTrainingChange={onEducationAndTrainingChange}
          onProfessionalPositionChange={onProfessionalPositionChange}
          onAddEducationAndTraining={onAddEducationAndTraining}
          onRemoveEducationAndTraining={onRemoveEducationAndTraining}
          onAddProfessionalPosition={onAddProfessionalPosition}
          onRemoveProfessionalPosition={onRemoveProfessionalPosition}
          onGrading1Change={onGrading1Change}
          onGrading2Change={onGrading2Change}
          onGrading3Change={onGrading3Change}
          onAddGrading1={onAddGrading1}
          onAddGrading2={onAddGrading2}
          onAddGrading3={onAddGrading3}
          onRemoveGrading1={onRemoveGrading1}
          onRemoveGrading2={onRemoveGrading2}
          onRemoveGrading3={onRemoveGrading3}
          onSaveReferentials={onSaveReferentials}
          hasIdConflicts={hasIdConflicts}
          showSaveSuccess={showSaveSuccess}
        />
      )}
    </Box>
  );
};

export default PaygapAnalysisParameters;
